import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, CheckCircle,
  Info, X, BookOpenCheck, Highlighter, HelpCircle,
  Film, PenLine, Brain, FileText, Repeat, Mic,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useUpdateTacheTimer } from '@/features/etudiants/hooks/use-agenda'
import { agendaApi } from '@/features/etudiants/api/agenda.api'
import { TopBar } from '@/design-system/layouts/top-bar'
import { darkenColor, lightenColor, chipStyle } from '@/lib/utils'
import { useDarkMode } from '@/shared/hooks/use-dark-mode'
import { soundVictoire } from '@/lib/sounds'

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

// ── Action visual config ─────────────────────────────────

interface ActionConfig {
  icon: LucideIcon
  phrase: string
  details: ReactNode[] | null
}

function buildActionConfig(t: (key: string) => string): Record<string, ActionConfig> {
  return {
    relire: {
      icon: BookOpenCheck,
      phrase: t('mobile.actionTimer.actions.relire.phrase'),
      details: null,
    },
    surligner: {
      icon: Highlighter,
      phrase: t('mobile.actionTimer.actions.surligner.phrase'),
      details: [
        <>Exemple : Le grand panda est une <mark className="rounded bg-yellow-200 px-0.5">espèce d'ours</mark>, que l'on appelle <mark className="rounded bg-yellow-200 px-0.5">ursidé</mark>. C'est un animal très <mark className="rounded bg-yellow-200 px-0.5">rare</mark> : on estime qu'il existe moins de <mark className="rounded bg-yellow-200 px-0.5">1000</mark> pandas <mark className="rounded bg-yellow-200 px-0.5">sauvages</mark> actuellement. L'espèce est <mark className="rounded bg-yellow-200 px-0.5">protégée</mark> depuis <mark className="rounded bg-yellow-200 px-0.5">1949</mark>.</>,
      ],
    },
    'se poser des questions': {
      icon: HelpCircle,
      phrase: t('mobile.actionTimer.actions.questions.phrase'),
      details: [
        t('mobile.actionTimer.actions.questions.detail1'),
        t('mobile.actionTimer.actions.questions.detail2'),
        t('mobile.actionTimer.actions.questions.detail3'),
      ],
    },
    scan: {
      icon: Film,
      phrase: t('mobile.actionTimer.actions.scan.phrase'),
      details: [
        t('mobile.actionTimer.actions.scan.detail1'),
        t('mobile.actionTimer.actions.scan.detail2'),
        t('mobile.actionTimer.actions.scan.detail3'),
      ],
    },
  }
}

function getActionConfig(libelle: string, actionConfig: Record<string, ActionConfig>): ActionConfig | null {
  const key = libelle.toLowerCase().trim()
  for (const [ruleKey, config] of Object.entries(actionConfig)) {
    if (key.includes(ruleKey) || ruleKey.includes(key)) return config
  }
  return null
}

// ── Detail tooltip modal ─────────────────────────────────

function DetailModal({
  config,
  actionName,
  actionColor,
  badgeColor,
  onClose,
}: {
  config: ActionConfig
  actionName: string
  actionColor: string
  badgeColor: string
  onClose: () => void
}) {
  const Icon = config.icon

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg animate-[slideUp_0.3s_ease-out] rounded-t-3xl bg-white pb-[max(env(safe-area-inset-bottom,16px),16px)] pt-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-neutral-200" />
        <button
          onClick={onClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-800"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="px-6 pb-4">
          <div className="flex flex-col items-center gap-2">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-2xl"
              style={{ backgroundColor: badgeColor }}
            >
              <Icon className="h-7 w-7 text-neutral-800" />
            </div>
            <h3 className="text-lg font-bold text-neutral-800">{actionName}</h3>
          </div>
          {config.details && (
            <div className="mt-4">
              <div className="space-y-1.5 text-center">
                {config.details.map((line, i) => (
                  <p key={i} className="text-sm leading-relaxed text-neutral-800">
                    {line}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────

export function ActionTimerMobilePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const updateTimer = useUpdateTacheTimer()
  const { t } = useTranslation()

  // URL params
  const actionLibelle = searchParams.get('action') || 'Action'
  const dureeMinutes = parseInt(searchParams.get('duree') || '15', 10)
  const actionCouleur = decodeURIComponent(searchParams.get('actionCouleur') || '#BBDEFB')
  const matieresParam = searchParams.get('matieres') || ''
  const couleursParam = searchParams.get('couleurs') || ''
  const tacheAgendaId = searchParams.get('tacheAgendaId')
    ? parseInt(searchParams.get('tacheAgendaId')!, 10)
    : null
  const activityId = searchParams.get('activityId')
    ? parseInt(searchParams.get('activityId')!, 10)
    : null
  const initialSeconds = searchParams.get('timerSeconds')
    ? parseInt(searchParams.get('timerSeconds')!, 10)
    : dureeMinutes * 60

  const matieres = matieresParam.split(',').map((m) => m.trim()).filter(Boolean)
  const couleurs = couleursParam
    ? decodeURIComponent(couleursParam).split(',').map((c) => c.trim())
    : matieres.map(() => actionCouleur)

  // Pastel = backgrounds, dark = text/icons
  const { isDark } = useDarkMode()
  const actionDark = isDark ? lightenColor(actionCouleur) : darkenColor(actionCouleur)

  const actionConfig = buildActionConfig(t)
  const config = getActionConfig(actionLibelle, actionConfig)
  const ActionIcon = config?.icon ?? Brain
  const [showDetail, setShowDetail] = useState(false)

  const [timeLeft, setTimeLeft] = useState(initialSeconds)
  const [isRunning, setIsRunning] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Wall-clock timer: enregistre l'heure de départ pour survivre à la mise en veille
  const startedAtRef = useRef<number>(0)
  const remainingAtStartRef = useRef<number>(initialSeconds)

  const totalSeconds = dureeMinutes * 60
  const elapsed = totalSeconds - timeLeft
  const progress = Math.min(100, Math.max(0, (elapsed / totalSeconds) * 100))

  // SVG circle
  const size = 250
  const strokeWidth = 10
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference - (progress / 100) * circumference

  const saveTimer = useCallback(
    (seconds: number) => {
      if (tacheAgendaId) {
        updateTimer.mutate({ tacheId: tacheAgendaId, timerSeconds: seconds })
      } else if (activityId) {
        agendaApi.updateActivityTimer(activityId, seconds).catch(() => { })
      }
    },
    [tacheAgendaId, activityId, updateTimer],
  )

  // Calcule le temps restant à partir du wall-clock
  const computeRemaining = useCallback(() => {
    const elapsedMs = Date.now() - startedAtRef.current
    return Math.max(0, Math.round(remainingAtStartRef.current - elapsedMs / 1000))
  }, [])

  // Countdown basé sur Date.now() — résiste à la mise en veille de l'écran
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        const remaining = computeRemaining()
        setTimeLeft(remaining)
        if (remaining <= 0) {
          setIsRunning(false)
          setIsCompleted(true)
          saveTimer(0)
          soundVictoire()
        }
      }, 1000)
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [isRunning, timeLeft, saveTimer, computeRemaining])

  // Recalcul immédiat quand l'écran se rallume (visibilitychange)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && isRunning) {
        const remaining = computeRemaining()
        setTimeLeft(remaining)
        if (remaining <= 0) {
          setIsRunning(false)
          setIsCompleted(true)
          saveTimer(0)
          soundVictoire()
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [isRunning, saveTimer, computeRemaining])

  // Auto-complete task via API when timer reaches 0
  useEffect(() => {
    if (isCompleted) {
      if (tacheAgendaId) {
        agendaApi.terminerTache(tacheAgendaId)
          .then(() => queryClient.invalidateQueries({ queryKey: ['agenda'] }))
          .catch(() => { })
      } else if (activityId) {
        agendaApi.terminerActivity(activityId, new Date())
          .then(() => queryClient.invalidateQueries({ queryKey: ['agenda'] }))
          .catch(() => { })
      }
    }
  }, [isCompleted, tacheAgendaId, activityId])

  // Tap on timer to toggle play/pause
  const handleTimerTap = () => {
    if (isCompleted) return
    const wasRunning = isRunning
    if (!wasRunning) {
      // Démarrage : enregistrer l'heure wall-clock et le restant actuel
      startedAtRef.current = Date.now()
      remainingAtStartRef.current = timeLeft
    } else {
      saveTimer(timeLeft)
    }
    setIsRunning(!isRunning)
  }

  const handleBack = async () => {
    try {
      if (tacheAgendaId) {
        await agendaApi.updateTacheTimer(tacheAgendaId, timeLeft)
      } else if (activityId) {
        await agendaApi.updateActivityTimer(activityId, timeLeft)
      }
      await queryClient.invalidateQueries({ queryKey: ['agenda'] })
    } catch {
      // Continue even on error
    }
    navigate('/dashboard')
  }

  const handleTerminer = async () => {
    if (isRunning) setIsRunning(false)
    try {
      if (tacheAgendaId) {
        await agendaApi.terminerTache(tacheAgendaId)
      } else if (activityId) {
        await agendaApi.terminerActivity(activityId, new Date())
      }
      await queryClient.invalidateQueries({ queryKey: ['agenda'] })
    } catch {
      // Continue navigation even on error
    }
    navigate('/dashboard')
  }

  return (
    <div
      className="flex min-h-[100dvh] flex-col"
      style={{ background: `linear-gradient(180deg, ${actionCouleur}70 0%, ${actionCouleur}30 50%, #ffffff 100%)` }}
    >
      {/* ── PEPS TopBar ── */}
      <TopBar />

      {/* ── Back button ── */}
      <div className="px-4 pt-3">
        <button
          onClick={handleBack}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-neutral-800 transition-colors active:bg-neutral-200"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      </div>

      {/* ── Header: icon → title → phrase → details ── */}
      <div className="px-6 text-center">
        <div
          className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full"
          style={{ backgroundColor: actionCouleur + '25' }}
        >
          <ActionIcon className="h-7 w-7 text-neutral-800" />
        </div>
        <h1 className="text-2xl font-bold text-neutral-800">
          {actionLibelle}
        </h1>
        {config?.phrase && (
          <p className="mt-1 text-base text-neutral-800">
            {config.phrase}
          </p>
        )}
        {config?.details && (
          <button
            onClick={() => setShowDetail(true)}
            className="mt-3 flex items-center gap-1.5 mx-auto rounded-full px-3 py-1.5 text-xs font-semibold transition-colors active:opacity-70"
            style={{ backgroundColor: actionCouleur + '20', color: '#262626', border: '1px solid #262626' }}
          >
            <Info className="h-3.5 w-3.5" />
            {t('mobile.actionTimer.moreDetails')}
          </button>
        )}
      </div>

      {/* ── Content ── */}
      <div className="flex flex-1 flex-col items-center px-6 pt-2">

        {/* Timer ring — tap to play/pause */}
        <div
          className="relative mt-5 cursor-pointer"
          style={{ width: size, height: size }}
          onClick={handleTimerTap}
        >
          <div
            className="absolute inset-3 rounded-full"
            style={{
              boxShadow: isCompleted
                ? '0 8px 40px rgba(16, 185, 129, 0.15)'
                : `0 8px 40px ${actionCouleur}20`,
            }}
          />

          <svg width={size} height={size} className="-rotate-90">
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#f1f5f9"
              strokeWidth={strokeWidth}
            />
            {!isCompleted && (
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="url(#timerGrad)"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                className="transition-[stroke-dashoffset] duration-1000 ease-linear"
              />
            )}
            {isCompleted && (
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="#10b981"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={0}
              />
            )}
            <defs>
              <linearGradient id="timerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={actionDark} />
                <stop offset="100%" stopColor={actionDark} stopOpacity={0.6} />
              </linearGradient>
            </defs>
          </svg>

          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {isCompleted ? (
              <>
                <CheckCircle className="h-12 w-12 text-emerald-500" />
                <span className="mt-2 text-xl font-bold text-emerald-600">{t('mobile.actionTimer.timerComplete')}</span>
              </>
            ) : (
              <>
                <span className="font-mono text-[3.2rem] font-extralight leading-none tracking-tight text-neutral-800">
                  {formatTime(timeLeft)}
                </span>
                <span className="mt-1.5 text-sm font-medium text-neutral-400">
                  {isRunning ? t('mobile.actionTimer.tapPause') : t('mobile.actionTimer.tapStart')}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Matières chips — matière colors */}
        <div className="mt-4 flex flex-wrap justify-center gap-1.5">
          {matieres.map((m, i) => {
            const c = couleurs[i] || actionCouleur
            return (
              <span
                key={i}
                className="rounded-full px-3 py-1 text-xs font-semibold"
                style={chipStyle(c, isDark)}
              >
                {m}
              </span>
            )
          })}
        </div>
      </div>

      {/* ── Bottom: J'ai terminé ── */}
      <div className="flex flex-col items-center gap-3 pb-[max(calc(env(safe-area-inset-bottom,16px)+20px),36px)] pt-4">
        <button
          onClick={handleTerminer}
          className="flex items-center gap-2 rounded-full px-7 py-3 text-sm font-bold shadow-lg transition-transform active:scale-95"
          style={{ backgroundColor: actionCouleur, color: '#262626', boxShadow: `0 10px 15px -3px ${actionCouleur}50` }}
        >
          <CheckCircle className="h-4 w-4" />
          {t('mobile.actionTimer.finish')}
        </button>
      </div>

      {/* Detail modal */}
      {showDetail && config && (
        <DetailModal
          config={config}
          actionName={actionLibelle}
          actionColor={actionCouleur}
          badgeColor={actionCouleur + 'C0'}
          onClose={() => setShowDetail(false)}
        />
      )}
    </div>
  )
}
